/**
 * Decide what to do after verifying a stored auth token.
 *
 * The old `checkAuth` collapsed every failure into "remove the token + go to
 * Login" because `Api.get` returns `null` for both a network failure and an
 * HTTP error. Offline that deleted a perfectly valid token and locked the user
 * out — the single biggest offline blocker (`docs/OFFLINE_MODE_PLAN.md`).
 *
 * This pure function separates the three cases so the caller can react
 * correctly. A genuine `401` logs out an invalid token; a `403` also logs out
 * because it proves that this account is not allowed in the selected
 * white-label distribution.
 */

/** Outcome of asking the API to verify the stored token. */
export type VerifyResult =
  | { ok: true; data: any }
  | { ok: false; networkError: true }
  | { ok: false; status: number };

export type AuthOutcome =
  /** Verified online — fully authenticated; persist this user. */
  | { action: "authenticated"; user: any }
  /** Token genuinely rejected (401) — clear it and send to Login. */
  | { action: "logout" }
  /** Couldn't verify (offline / transient 5xx) — trust the stored token. */
  | { action: "offline" };

export function decideAuth(result: VerifyResult): AuthOutcome {
  // Confirmed valid token with a user payload.
  if (result.ok && result.data?.user) {
    return { action: "authenticated", user: result.data.user };
  }

  // Hard logout: the token is invalid, or it is forbidden for this fixed
  // white-label tenant. Never mistake a tenant mismatch for being offline.
  if (!result.ok && "status" in result && [401, 403].includes(result.status)) {
    return { action: "logout" };
  }

  // Everything else — no network response, a 5xx, or a malformed 200 — is
  // non-fatal: keep the token and let the user keep working (provisional auth).
  return { action: "offline" };
}
